import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
/**
 * Portal module ("Portalzugänge") — the Stammdaten → Kommunikation → Portal
 * screen. Renders the StammdatenSidebar + a content area with two donut summary
 * cards, a filter row, status tabs, and a contacts table, plus a floating
 * "Kundenportal-App" phone overlay. Styling is 1:1 with the prototype
 * (Hausverwaltung Hero.dc.html, content ~1047, overlay ~1916).
 */
import { Building2, ChevronDown, Search } from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { PortalDonuts } from "./module-portal-donuts";
import { PortalTable } from "./module-portal-table";

function statusTabs(t: Translate): readonly {
  readonly label: string;
  readonly count: string;
  readonly active?: boolean;
}[] {
  return [
    { label: t("offen"), count: "232", active: true },
    { label: t("ausstehend"), count: "386" },
    { label: t("abgelaufen"), count: "42" },
    { label: t("zugang"), count: "1.204" },
    { label: t("eMail"), count: "174" },
  ];
}

/** Filter row: Liegenschaft dropdown + Alle/Mieter/Eigentümer segmented + search. */
function FilterRow(): ReactNode {
  const t = useTranslations("marketing.modulePortal");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        marginBottom: 13,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 35,
          padding: "0 12px",
          border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
          borderRadius: 9,
          fontSize: 13,
          color: ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
          flexShrink: 0,
        }}
      >
        <span style={{ color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"), display: "inline-flex" }}>
          <Building2 width={15} height={15} strokeWidth={1.8} />
        </span>
        {t("liegenschaft")}
        <span style={{ color: ld("rgba(11,15,25,0.35)", "rgba(250,250,250,0.35)"), display: "inline-flex" }}>
          <ChevronDown width={14} height={14} strokeWidth={1.8} />
        </span>
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: 3,
          background: ld("#F1F2F4", "#2b2e32"),
          borderRadius: 9,
          gap: 2,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            padding: "5px 13px",
            borderRadius: 7,
            background: ld("#fff", "#181b1f"),
            boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.09)", "rgba(0,0,0,0.185)")}`,
            fontSize: 12.5,
            fontWeight: 600,
            color: H.ink,
          }}
        >
          {t("alle")}
        </span>
        <span
          style={{
            padding: "5px 13px",
            fontSize: 12.5,
            color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
          }}
        >
          {t("mieter")}
        </span>
        <span
          style={{
            padding: "5px 13px",
            fontSize: 12.5,
            color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
          }}
        >
          {t("eigentuemer")}
        </span>
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          height: 35,
          padding: "0 12px",
          border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
          borderRadius: 9,
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          fontSize: 13,
        }}
      >
        <Search width={15} height={15} strokeWidth={1.8} />
        {t("nameEMailSuchen")}
      </span>
    </div>
  );
}

/** Status tabs row (Offen / Ausstehend / Abgelaufen / Zugang / Ohne E-Mail). */
function StatusTabs(): ReactNode {
  const t = useTranslations("marketing.modulePortal");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        borderBottom: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
      }}
    >
      {statusTabs(t).map((t) => {
        const active = t.active === true;
        return (
          <span
            key={t.label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              paddingBottom: 9,
              borderBottom: active ? `2px solid ${ld("#0B0F19", "#fafafa")}` : undefined,
              marginBottom: active ? -1 : undefined,
              fontSize: 13,
              fontWeight: active ? 600 : undefined,
              color: active ? H.ink : ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
            }}
          >
            {t.label}
            <span
              style={{
                fontSize: 11.5,
                color: active ? ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") : ld("rgba(11,15,25,0.35)", "rgba(250,250,250,0.35)"),
              }}
            >
              {t.count}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** The Portal module interior: StammdatenSidebar + content (phone overlay is
 *  hoisted to the frame via the registry `Overlay` slot). */
export function ModulePortal(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.modulePortal");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="portal" setActive={props.setActive} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>
            {t("portalzugaenge")}
          </span>
          <span style={{ fontSize: 13, color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)") }}>
            {t("ueberAlleLiegenschaften")}
          </span>
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
            margin: "3px 0 14px",
          }}
        >
          {t("k312Liegenschaften1864")}
        </div>

        <PortalDonuts />
        <FilterRow />
        <StatusTabs />
        <PortalTable />
      </div>
    </div>
  );
}
